import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  HiOutlinePencilSquare, 
  HiOutlinePaperAirplane, 
  HiOutlineArrowLeft,
  HiOutlineChatBubbleLeftRight,
  HiOutlineMagnifyingGlass,
  HiOutlineXMark,
  HiOutlineTrash
} from "react-icons/hi2";
import { toast } from "react-toastify";
import { messagingService, userService, authService } from "../services";
import SharedModal from "../components/common/SharedModal";
import "./MessagingPage.css";

const MessagingPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const currentUser = authService.getUser();
  const currentUserId = currentUser?.userId;

  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(conversationId ? parseInt(conversationId) : null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // New Conversation Modal State
  const [showNewModal, setShowNewModal] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [newSubject, setNewSubject] = useState("");
  
  // Delete Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [convToDelete, setConvToDelete] = useState(null);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const fetchConversations = useCallback(async () => {
    try {
      const data = await messagingService.getConversations();
      setConversations(data || []);
    } catch (err) {
      console.error("Failed to load conversations:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMessages = useCallback(async (id) => {
    try {
      setMessagesLoading(true);
      const data = await messagingService.getMessages(id);
      setMessages(data?.items || []);
      // Mark as read when opening
      await messagingService.markConversationRead(id);
    } catch (err) {
      console.error("Failed to load messages:", err);
    } finally {
      setMessagesLoading(false);
      setTimeout(scrollToBottom, 50);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 30000); // Refresh list every 30s
    return () => clearInterval(interval);
  }, [fetchConversations]);

  useEffect(() => {
    if (activeConvId) {
      fetchMessages(activeConvId);
      // Poll for new messages every 10s if active
      const interval = setInterval(() => {
        messagingService.getMessages(activeConvId).then(data => {
          setMessages(data?.items || []);
        });
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [activeConvId, fetchMessages]);

  useEffect(() => {
    if (conversationId) {
      setActiveConvId(parseInt(conversationId));
    }
  }, [conversationId]);

  const handleSelectConversation = (id) => {
    setActiveConvId(id);
    navigate(`/home/messaging/${id}`);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !activeConvId) return;
    try {
      const sent = await messagingService.sendMessage(activeConvId, newMessage);
      setMessages(prev => [...prev, sent]);
      setNewMessage("");
      setTimeout(scrollToBottom, 50);
      // Refresh sidebar to update last preview
      fetchConversations();
    } catch (err) {
      toast.error("Failed to send message.");
    }
  };

  const openNewModal = async () => {
    try {
      setShowNewModal(true);
      if (tenantUsers.length === 0) {
        const users = await userService.getAllUsers();
        setTenantUsers(users.filter(u => u.userId !== currentUserId));
      }
    } catch (err) {
      toast.error("Could not load users.");
    }
  };

  const handleCreateConversation = async () => {
    if (selectedUserIds.length === 0) return;
    try {
      const newConv = await messagingService.createConversation({
        participantIds: selectedUserIds,
        subject: newSubject.trim() || null
      });
      setShowNewModal(false);
      setSelectedUserIds([]);
      setNewSubject("");
      fetchConversations();
      handleSelectConversation(newConv.conversationId);
    } catch (err) {
      toast.error("Failed to start conversation.");
    }
  };

  const handleDeleteClick = (ev, id) => {
    ev.stopPropagation();
    setConvToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!convToDelete) return;
    try {
      await messagingService.deleteConversation(convToDelete);
      toast.success("Conversation deleted");
      if (activeConvId === convToDelete) {
        setActiveConvId(null);
        navigate("/home/messaging");
      }
      setShowDeleteModal(false);
      setConvToDelete(null);
      fetchConversations();
    } catch (err) {
      toast.error("Failed to delete conversation.");
    }
  };

  const filteredConversations = useMemo(() => {
    return conversations.filter(c => {
      const subj = c.subject?.toLowerCase() || "";
      const lastMsg = c.lastMessage?.body?.toLowerCase() || "";
      const participants = c.participants?.map(p => p.fullName.toLowerCase()).join(" ") || "";
      return subj.includes(searchTerm.toLowerCase()) || 
             lastMsg.includes(searchTerm.toLowerCase()) ||
             participants.includes(searchTerm.toLowerCase());
    });
  }, [conversations, searchTerm]);

  const activeConv = conversations.find(c => c.conversationId === activeConvId);

  const getOtherParticipantsNames = (conv) => {
    return conv.participants
      ?.filter(p => p.userId !== currentUserId)
      ?.map(p => p.fullName)
      ?.join(", ") || "Unknown";
  };

  return (
    <div className="messaging-page">
      {/* Sidebar - Conversation List */}
      <aside className={`msg-sidebar ${activeConvId ? "hide-on-mobile" : ""}`}>
        <div className="msg-sidebar-header">
          <h2>Inbox</h2>
          <button className="btn-new-msg" onClick={openNewModal}>
            <HiOutlinePencilSquare /> New
          </button>
        </div>

        <div className="msg-search-box">
          <HiOutlineMagnifyingGlass className="search-icon" />
          <input 
            type="text" 
            placeholder="Search chat history..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="msg-list">
          {loading ? (
            <div className="msg-list-loading">Syncing...</div>
          ) : filteredConversations.length === 0 ? (
            <div className="msg-list-empty">
              <HiOutlineChatBubbleLeftRight />
              <p>No messages found</p>
            </div>
          ) : (
            filteredConversations.map(conv => (
              <div key={conv.conversationId} className="msg-item-wrapper">
                <button 
                  className={`msg-item-btn ${activeConvId === conv.conversationId ? "active" : ""}`}
                  onClick={() => handleSelectConversation(conv.conversationId)}
                >
                  <div className="msg-item-avatar">
                    {conv.participants?.[0]?.fullName?.charAt(0) || "C"}
                  </div>
                  <div className="msg-item-info">
                    <div className="msg-item-line1">
                      <span className="msg-item-name">{conv.subject || getOtherParticipantsNames(conv)}</span>
                      <time>{new Date(conv.lastMessage?.sentAt || conv.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
                    </div>
                    <div className="msg-item-line2">
                      <p className="msg-item-preview">{conv.lastMessage?.body || "Start a conversation"}</p>
                      {conv.unreadCount > 0 && <span className="unread-dot">{conv.unreadCount}</span>}
                    </div>
                  </div>
                </button>
                <button 
                  className="btn-delete-conv" 
                  title="Delete conversation"
                  onClick={(ev) => handleDeleteClick(ev, conv.conversationId)}
                >
                  <HiOutlineTrash />
                </button>
              </div>
            ))
          )}
        </div>
      </aside>

      {/* Main Panel - Thread */}
      <main className={`msg-thread-container ${!activeConvId ? "hide-on-mobile" : ""}`}>
        {activeConvId ? (
          <>
            <div className="thread-header">
              <button className="btn-back" onClick={() => setActiveConvId(null)}>
                <HiOutlineArrowLeft />
              </button>
              <div className="thread-header-info">
                <h3>{activeConv?.subject || getOtherParticipantsNames(activeConv || {})}</h3>
                <span className="status-online">In conversation</span>
              </div>
            </div>

            <div className="thread-messages">
              {messagesLoading && messages.length === 0 ? (
                <div className="thread-loading">Decrypting messages...</div>
              ) : messages.length === 0 ? (
                <div className="thread-empty">
                  <p>Send your first message to start the conversation.</p>
                </div>
              ) : (
                messages.map((m) => {
                  const isMine = m.senderId === currentUserId;
                  return (
                    <div key={m.messageId} className={`msg-bubble-wrap ${isMine ? "own" : ""}`}>
                      {!isMine && <span className="bubble-sender">{m.senderName}</span>}
                      <div className="bubble-content">
                        <p>{m.body}</p>
                        <time>{new Date(m.sentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</time>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="thread-input-area">
              <div className="thread-input-box">
                <input 
                  type="text" 
                  placeholder="Type your message here..." 
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                />
                <button 
                  className="btn-send-msg" 
                  onClick={handleSend}
                  disabled={!newMessage.trim()}
                >
                  <HiOutlinePaperAirplane />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="thread-placeholder">
            <HiOutlineChatBubbleLeftRight />
            <h2>Direct Messaging</h2>
            <p>Select a contact from the sidebar to view your message history or start a new conversation.</p>
          </div>
        )}
      </main>

      {/* New Conv Modal */}
      <SharedModal 
        isOpen={showNewModal} 
        onClose={() => setShowNewModal(false)}
        title="Start New Conversation"
        customWidth="500px"
      >
        <div className="modal-body messaging-modal">
          <div className="form-group">
            <label>Subject (Optional)</label>
            <input 
              type="text" 
              className="form-control" 
              value={newSubject} 
              onChange={(e) => setNewSubject(e.target.value)}
              placeholder="e.g. Project Updates"
            />
          </div>
          <div className="form-group">
            <label>Select Participants</label>
            <div className="participants-selector">
              {tenantUsers.map(u => (
                <label key={u.userId} className={`participant-label ${selectedUserIds.includes(u.userId) ? "active" : ""}`}>
                  <input 
                    type="checkbox" 
                    checked={selectedUserIds.includes(u.userId)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedUserIds([...selectedUserIds, u.userId]);
                      else setSelectedUserIds(selectedUserIds.filter(id => id !== u.userId));
                    }}
                  />
                  <span>{u.fullName}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn-cancel" onClick={() => setShowNewModal(false)}>Cancel</button>
          <button 
            className="btn-primary" 
            onClick={handleCreateConversation}
            disabled={selectedUserIds.length === 0}
          >
            Create Chat
          </button>
        </div>
      </SharedModal>

      {/* Delete Confirmation Modal */}
      <SharedModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Conversation"
        customWidth="400px"
      >
        <div className="modal-body text-center p-4">
          <div className="delete-icon-circle mb-3">
            <HiOutlineTrash />
          </div>
          <p className="mb-0">Are you sure you want to delete this conversation?</p>
          <p className="text-secondary small">This will permanently remove all messages for all participants.</p>
        </div>
        <div className="modal-footer justify-content-center">
          <button className="btn-cancel" onClick={() => setShowDeleteModal(false)}>Cancel</button>
          <button className="btn-danger" onClick={confirmDelete}>Delete Permanently</button>
        </div>
      </SharedModal>
    </div>
  );
};

export default MessagingPage;
